import {
  addCurrentAuthHeader,
  type SessionService,
} from '@efdigital/ll-addon-auth';
import type { Apis } from '@efdigital/ll-addon-auth/types/apis';
import type {
  CoachingSession,
  CoachingSessionStatus,
} from '@efdigital/ll-addon-data';
import type {
  CoachGetCoacheesResponseBody,
  CoachingSessionResponse,
  CoachPostAttendanceMarkRequestBody,
  CoachPostAttendanceMarkResponseBody,
  CoachPostCancelSessionRequestBody,
  CoachPostCancelSessionResponseBody,
  CoachPostRejectSessionRequestBody,
} from '@efdigital/ll-cdk-coaching-sessions-api-types';

type CoachingSessionResponseWithStatus = Omit<
  CoachingSessionResponse,
  'status'
> & {
  status: CoachingSessionStatus;
};

type CoachPostConfirmSessionResponseBody = {
  session: CoachingSessionResponseWithStatus;
};

type CoachPostRejectSessionResponseBody = {
  session: CoachingSessionResponseWithStatus;
};

export async function buildCoachGetCoacheesRequest(
  apis: Apis,
  session: SessionService
) {
  const init = await addCurrentAuthHeader(
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    },
    { session }
  );

  return new Request(
    `${apis.systemsApi.host}/ll-coaching-sessions/coach/coachees`,
    init
  );
}

export async function parseCoachGetCoacheesResponse(
  response: Response
): Promise<CoachGetCoacheesResponseBody> {
  if (!response.ok) {
    throw new Error('Failed to fetch coachees');
  }

  const data = (await response.json()) as CoachGetCoacheesResponseBody;
  return data;
}

export async function buildCoachCancelSessionRequest(
  apis: Apis,
  session: SessionService,
  coachingSession: CoachingSession,
  body: CoachPostCancelSessionRequestBody
) {
  const init = await addCurrentAuthHeader(
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    },
    { session }
  );

  return new Request(
    `${apis.systemsApi.host}/ll-coaching-sessions/coach/coaching-sessions/${encodeURIComponent(coachingSession.id!)}/cancel`,
    init
  );
}

export async function parseCoachCancelSessionResponse(
  response: Response
): Promise<CoachPostCancelSessionResponseBody> {
  if (!response.ok) {
    throw new Error('Failed to cancel session at short notice');
  }

  const data = (await response.json()) as CoachPostCancelSessionResponseBody;
  return data;
}

export async function buildCoachMarkAttendanceRequest(
  apis: Apis,
  session: SessionService,
  coachingSession: CoachingSession,
  body: CoachPostAttendanceMarkRequestBody
) {
  const init = await addCurrentAuthHeader(
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    },
    { session }
  );

  return new Request(
    `${apis.systemsApi.host}/ll-coaching-sessions/coach/coaching-sessions/${encodeURIComponent(coachingSession.id!)}/attendance`,
    init
  );
}

export async function parseCoachMarkAttendanceResponse(
  response: Response
): Promise<CoachPostAttendanceMarkResponseBody> {
  if (!response.ok) {
    throw new Error('Failed to mark attendance');
  }

  const data = (await response.json()) as CoachPostAttendanceMarkResponseBody;
  return data;
}

export async function buildCoachConfirmSessionRequest(
  apis: Apis,
  session: SessionService,
  coachingSession: CoachingSession
) {
  const init = await addCurrentAuthHeader(
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    },
    { session }
  );

  return new Request(
    `${apis.systemsApi.host}/ll-coaching-sessions/coach/coaching-sessions/${encodeURIComponent(coachingSession.id!)}/confirm`,
    init
  );
}

export async function parseCoachConfirmSessionResponse(
  response: Response
): Promise<CoachPostConfirmSessionResponseBody> {
  if (!response.ok) {
    throw new Error('Failed to confirm session');
  }

  const data = (await response.json()) as CoachPostConfirmSessionResponseBody;
  return data;
}

export async function buildCoachRejectSessionRequest(
  apis: Apis,
  session: SessionService,
  coachingSession: CoachingSession,
  body: CoachPostRejectSessionRequestBody
) {
  const init = await addCurrentAuthHeader(
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    },
    { session }
  );

  return new Request(
    `${apis.systemsApi.host}/ll-coaching-sessions/coach/coaching-sessions/${encodeURIComponent(coachingSession.id!)}/reject`,
    init
  );
}

export async function parseCoachRejectSessionResponse(
  response: Response
): Promise<CoachPostRejectSessionResponseBody> {
  if (!response.ok) {
    throw new Error('Failed to decline session');
  }

  const data = (await response.json()) as CoachPostRejectSessionResponseBody;
  return data;
}
